import Reveal from "../components/Reveal";
import SectionHeading from "../components/SectionHeading";
import RfqBanner from "../components/RfqBanner";

export const PageBanner = ({ eyebrow, title, description, testId }) => (
  <section data-testid={testId} className="bg-[#F8FAFC] border-b border-slate-200">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 md:py-20">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#2563EB]">{eyebrow}</p>
      <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">
        {title}
      </h1>
      {description && (
        <p className="mt-4 max-w-2xl text-base text-slate-600 leading-relaxed">{description}</p>
      )}
    </div>
  </section>
);

const CAPABILITY_AREAS = [
  {
    title: "Tooling",
    desc: "Production tooling manufactured to customer requirements, including press tools and cutting Dies.",
  },
  {
    title: "Dies",
    desc: "Gasket Dies, die plates and mould bases machined to drawing dimensions and profiles.",
  },
  {
    title: "Machined Components",
    desc: "Shafts, rollers, conveyor rollers, pulleys and rings machined to specified dimensions and finishes.",
  },
  {
    title: "Custom Industrial Components",
    desc: "Drawing-based components such as wire grippers and customer-specific precision parts.",
  },
];

const About = () => (
  <>
    <PageBanner
      testId="about-banner"
      eyebrow="About Us"
      title="About Radiant Machine Tools"
      description="A precision engineering and manufacturing company serving industrial customers since 1995."
    />

    <section data-testid="about-overview" className="py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <SectionHeading eyebrow="Company Overview" title="Established in 1995" />
          <p className="mt-4 text-base text-slate-600 leading-relaxed">
            Radiant Machine Tools is an engineering and manufacturing company established in 1995,
            based in Hyderabad, Telangana, India. For three decades, the company has focused on one
            discipline:
            manufacturing tooling, Dies, machined components and custom industrial parts that match
            customer drawings and technical specifications.
          </p>
          <p className="mt-4 text-base text-slate-600 leading-relaxed">
            Our customers are industrial buyers, procurement teams, engineers, OEMs and manufacturing
            companies that need components made correctly to specification — not off-the-shelf
            approximations.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <img
            src="/images/cnc-machining.jpg"
            alt="CNC machining operations at a precision manufacturing workshop"
            className="w-full aspect-[4/3] object-cover border border-slate-200"
            loading="lazy"
          />
        </Reveal>
      </div>
    </section>

    <section data-testid="about-approach" className="py-16 md:py-24 bg-[#F8FAFC] border-y border-slate-200">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <Reveal className="order-2 lg:order-1">
          <img
            src="/images/turning.jpg"
            alt="Turning operation producing a cylindrical precision component"
            className="w-full aspect-[4/3] object-cover border border-slate-200"
            loading="lazy"
          />
        </Reveal>
        <Reveal delay={0.1} className="order-1 lg:order-2">
          <SectionHeading eyebrow="Our Approach" title="Manufactured to Your Drawing" />
          <p className="mt-4 text-base text-slate-600 leading-relaxed">
            Every job begins with the customer's requirement: a drawing or technical specification
            defining dimensions, tolerances, material and surface finish. Our role is to manufacture
            exactly that — reviewing the specification, planning the machining operations, and
            producing the component to the agreed requirements.
          </p>
          <p className="mt-4 text-base text-slate-600 leading-relaxed">
            This drawing-driven approach means customers receive parts that fit their assemblies and
            processes, whether the requirement is a single precision component or production tooling.
          </p>
        </Reveal>
      </div>
    </section>

    <section data-testid="about-capability" className="py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <SectionHeading
            eyebrow="Engineering Capability"
            title="What We Manufacture"
            description="Our work spans four areas of precision manufacturing."
          />
        </Reveal>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {CAPABILITY_AREAS.map((area, i) => (
            <Reveal key={area.title} delay={i * 0.05}>
              <div className="h-full border border-slate-200 bg-white p-6 rounded-sm">
                <span className="text-xs font-mono font-semibold text-[#2563EB]">0{i + 1}</span>
                <h3 className="mt-3 text-base font-semibold text-slate-900">{area.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{area.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section data-testid="about-quality" className="py-16 md:py-24 bg-[#F8FAFC] border-t border-slate-200">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 max-w-3xl lg:max-w-7xl">
        <Reveal>
          <SectionHeading eyebrow="Precision & Quality" title="Checked Against the Drawing" />
          <p className="mt-4 text-base text-slate-600 leading-relaxed max-w-3xl">
            Components are verified against drawing dimensions and surface-finish requirements
            during production and before dispatch. Dimensional checks are carried out as part of
            the manufacturing process, so parts leaving the works match the specification they
            were ordered to.
          </p>
          <p className="mt-4 text-base text-slate-600 leading-relaxed max-w-3xl">
            Specific inspection reports, material certificates and quality documentation can be
            discussed and agreed at the enquiry stage based on your requirement.
          </p>
        </Reveal>
      </div>
    </section>

    <RfqBanner />
  </>
);

export default About;
